import {
	Code as ChevronUpDown,
	CrossCircle,
	EyeSlash,
	SortText,
	SortTextReverse,
} from "@layer/icons";
import { cn } from "@layer/utils/cn";
import type { Column } from "@tanstack/react-table";
import type { HTMLAttributes } from "react";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "../../../components/dropdown-menu";

type DataTableColumnHeaderProps<TData, TValue> =
	HTMLAttributes<HTMLDivElement> & {
		column: Column<TData, TValue>;
		title: string;
	};

const ColumnHeader = <TData, TValue>({
	column,
	title,
	className,
}: DataTableColumnHeaderProps<TData, TValue>) => {
	if (!column.getCanSort()) {
		return <div className={cn(className)}>{title}</div>;
	}

	return (
		<div className={cn("flex items-center space-x-2", className)}>
			<DropdownMenu>
				<DropdownMenuTrigger
					className={cn(
						column.getIsSorted()
							? "text-primary-text"
							: "text-accent-text-contrast",
						"flex items-center text-sm outline-none group",
					)}
				>
					<>
						<span>{title}</span>
						{column.getIsSorted() === "desc" ? (
							<SortTextReverse
								className="ml-2
              transition-colors text-accent-text"
							/>
						) : column.getIsSorted() === "asc" ? (
							<SortText
								className="ml-2
              transition-colors text-accent-text"
							/>
						) : (
							<ChevronUpDown className="ml-2 group-hover:text-accent-text-contrast transition-colors rotate-90 text-accent-text" />
						)}
					</>
				</DropdownMenuTrigger>
				<DropdownMenuContent
					align="start"
					side="right"
					className="flex gap-x-1 min-w-max"
				>
					<DropdownMenuItem
						disabled={column.getIsSorted() === "asc"}
						onClick={() => column.toggleSorting(false)}
					>
						<SortText variant="filled" />
					</DropdownMenuItem>
					<DropdownMenuItem
						disabled={column.getIsSorted() === "desc"}
						onClick={() => column.toggleSorting(true)}
					>
						<SortTextReverse variant="filled" />
					</DropdownMenuItem>
					<DropdownMenuItem
						onClick={() => column.toggleVisibility(false)}
					>
						<EyeSlash variant="filled" />
					</DropdownMenuItem>
					{column.getIsSorted() && (
						<DropdownMenuItem onClick={() => column.clearSorting()}>
							<CrossCircle
								variant="filled"
								className="text-danger-text"
							/>
						</DropdownMenuItem>
					)}
				</DropdownMenuContent>
			</DropdownMenu>
		</div>
	);
};

export { ColumnHeader };
